import { MainService, MissionJob, RobotReadMissionJobInline, RobotReadOdysseyInline } from 'api/schema'
import {
  MISSION_JOB_STATUSES,
  ROBOT_COMMANDS,
  ROBOT_EQUIPMENT_COMMAND,
  ROBOT_EQUIPMENT_STATES,
  ROBOT_MODES,
  ROBOT_NETWORK_STATUSES,
  ROBOT_STATUSES,
} from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'
import { VENDOR } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'

import { MissionJobTypeEnum, Odyssey, RobotRead, SchedulerSettings } from './schema'

export interface RobotTypeDefinitionItem {
  id: number
  code: string
  display_name: string
  vendor_code: string
  vendor_display_name: string
}

/** Mission.robot_type value for API (Sweeper, Combo). Only Autonomics types are used in missions. */
export interface MissionRobotTypeOption {
  display_name: string
  robot_type: string
}

const AUTONOMICS_MISSION_CODES = new Set(['Sweeper', 'Combo'])

// Metadata for map recording mode (Mapping mode)
export interface MappingMetadata {
  name?: string | null
  pose?: {
    lon: number
    lat: number
    yaw: number // In degrees, range (-180, 180], 0 = east
  } | null
  continue_on_the_facility?: boolean
  update_map?: boolean
}

const normalizeRobot = (robotRaw: RobotRead): IRobotInfo => {
  let mission_job: RobotReadMissionJobInline | RobotReadOdysseyInline | undefined = undefined

  // Extract locations from robotRaw (direct field, not in schema yet)
  // Ensure it's always an array or null/undefined, even if backend sends unexpected type
  const locationsRaw = (robotRaw as any).locations
  const locations = Array.isArray(locationsRaw) ? locationsRaw : locationsRaw ?? null

  return {
    id: robotRaw.id,
    serialNumber: robotRaw.serial_number,
    robot_type: robotRaw.robot_type,
    robot_type_display: robotRaw.robot_type_display || robotRaw.robot_type,
    name: robotRaw.name || '',
    description: robotRaw.description || '',
    createdAt: robotRaw.created_at,
    updatedAt: robotRaw.updated_at,
    status: ROBOT_STATUSES.AVAILABLE,
    robotRaw: robotRaw,
    sim_card_number: robotRaw.sim_card_number || '',
    robot_color: robotRaw.robot_color || '',
    activeMode: robotRaw.active_mode as ROBOT_MODES,
    activeCommand: robotRaw.active_command as ROBOT_COMMANDS,
    job: robotRaw.last_job_id
      ? {
          id: robotRaw.last_job_id,
          type: robotRaw.last_task_type,
          missionSequenceNumber: robotRaw.last_mission_sequence_number,
          missionName: robotRaw.last_mission_name || '',
          missionId: robotRaw.last_mission || 0,
          status: ((mission_job as any)?.status as MISSION_JOB_STATUSES) || MISSION_JOB_STATUSES.OK,
        }
      : undefined,
    networkStatus: (robotRaw.network_status as ROBOT_NETWORK_STATUSES) || ROBOT_NETWORK_STATUSES.OFFLINE,
    stationId: robotRaw.station_id || undefined,
    lastPosition: robotRaw.telemetry_last_point,
    lastOrientation: robotRaw.telemetry_last_orientation as { x: number; y: number; z: number; w: number },
    vendor: (robotRaw.vendor || VENDOR.AUTONOMICS) as VendorCode,
    locations: locations,
    batteryPower: robotRaw.battery_power,
    isCharging: (robotRaw as any).is_charging,
    systemDiagnostics: robotRaw.system_diagnostics,
    emergencyButton:
      robotRaw.equipment_state === ROBOT_EQUIPMENT_STATES.EMERGENCY ||
      (robotRaw as any).telemetry_last?.locks?.danger_button === true,
  }
}

export const RobotApi = {
  fetchRobotTypeDefinitions: async (vendorCode: VendorCode): Promise<RobotTypeDefinitionItem[]> => {
    const params = new URLSearchParams({ vendor__code: vendorCode })
    const response = await fetch(`/api/main/robot-type-definition/?${params}`, { credentials: 'include' })
    if (!response.ok) return []
    const data = await response.json()
    return data.results ?? data ?? []
  },

  /** Fetch robot type definitions for mission creation (only Autonomics). Returns options with display_name and robot_type for API. */
  fetchMissionRobotTypeOptions: async (): Promise<MissionRobotTypeOption[]> => {
    const items = await RobotApi.fetchRobotTypeDefinitions(VENDOR.AUTONOMICS)
    return items
      .filter(item => AUTONOMICS_MISSION_CODES.has(item.code))
      .map(item => ({ display_name: item.display_name, robot_type: item.code }))
  },
  fetchRobots: async (
    page?: number,
    pageSize?: number,
    search?: string,
    ids?: number[]
  ): Promise<{ results: IRobotInfo[]; count: number }> => {
    try {
      const robots = await MainService.mainRobotList(undefined, page, pageSize, search, ids)
      if (!robots.results) {
        return { results: [], count: 0 }
      }
      const normalizedRobots = robots.results.map(robotRaw => normalizeRobot(robotRaw))
      return { results: normalizedRobots, count: robots.total || 0 }
    } catch (error) {
      // Network errors (ERR_NETWORK_CHANGED, Failed to fetch) are common and expected
      // when connection is unstable - re-throw them silently for callers to handle
      if (error instanceof TypeError && (error.message === 'Failed to fetch' || error.message.includes('network'))) {
        throw error
      }
      // Log other errors (server errors, validation errors, etc.)
      console.error('[RobotApi] fetchRobots error:', error)
      throw error
    }
  },
  createRobot: async (
    name: string,
    description: string,
    serialNumber: string,
    stationId?: number | null,
    sim_card_number?: string | null,
    robotType?: string | null,
    robotColor?: string | number | null,
    vendor?: VendorCode
  ): Promise<boolean> => {
    const body: Record<string, unknown> = {
      name,
      description,
      users_ids: [],
      serial_number: serialNumber,
      station_id: stationId,
      sim_card_number: sim_card_number,
      robot_type: robotType,
      robot_color: robotColor,
      vendor: vendor || VENDOR.AUTONOMICS,
    }
    const robot = await MainService.mainRobotCreate(body as any)
    if (robot) {
      return true
    }
    return false
  },
  patchRobot: async (
    id: number,
    name: string,
    description: string,
    stationId?: number,
    sim_card_number?: string | null,
    robotType?: string | null,
    robotColor?: string | number | null,
    vendor?: VendorCode
  ): Promise<boolean> => {
    const body: Record<string, unknown> = {
      name,
      description,
      station_id: stationId,
      sim_card_number: sim_card_number,
    }
    if (robotType !== undefined) body.robot_type = robotType
    if (robotColor !== undefined) body.robot_color = robotColor
    if (vendor !== undefined) body.vendor = vendor
    const robot = await MainService.mainRobotPartialUpdate(id, body as any)
    if (robot) {
      return true
    }
    return false
  },
  fetchRobot: async (id: number): Promise<IRobotInfo> => {
    try {
      const robotRaw = await MainService.mainRobotRetrieve(id)

      // Check if robotRaw is valid before normalization
      // If robotRaw is undefined after a successful request (status 200), it's likely a network issue
      // (ERR_NETWORK_CHANGED can return 200 but with invalid/empty data)
      if (!robotRaw) {
        // Treat as network error - re-throw as TypeError to be handled by callers
        const networkError = new TypeError('Failed to fetch')
        throw networkError
      }

      const robot = normalizeRobot(robotRaw)
      return robot
    } catch (error) {
      // Network errors (ERR_NETWORK_CHANGED, Failed to fetch) are common and expected
      // when connection is unstable - don't log them as errors
      if (error instanceof TypeError && (error.message === 'Failed to fetch' || error.message.includes('network'))) {
        // Re-throw network errors silently - they will be handled by callers
        throw error
      }
      // Log other errors (server errors, validation errors, etc.)
      console.error('[RobotApi] fetchRobot error:', error)
      throw error
    }
  },
  deleteRobot: async (id: number): Promise<void> => {
    await MainService.mainRobotDestroy(id)
  },
  createJob: async (
    missionId: number,
    robotId: number,
    scheduler_settings?: SchedulerSettings,
    name?: string
  ): Promise<MissionJob> => {
    const task = await MainService.mainMissionJobCreate({
      mission_id: missionId,
      robot_id: robotId,
      scheduler_settings: scheduler_settings ?? null,
      name: name ?? null,
    })
    return task
  },

  createOdyssey: async (
    ArrayMissionId: number[],
    robotId: number,
    scheduler_settings?: SchedulerSettings,
    name?: string,
    scenario?: number | null
  ): Promise<Odyssey> => {
    const task = await MainService.mainOdysseyCreate({
      missions: ArrayMissionId,
      robot_id: robotId,
      job_type: MissionJobTypeEnum.SCENARIO,
      scheduler_settings: scheduler_settings ?? null,
      name: name ?? null,
      scenario: scenario,
    })
    return task
  },

  updateTheEntireJob: async (
    task_id: number,
    missionId: number,
    robotId: number,
    scheduler_settings?: SchedulerSettings,
    name?: string,
    status: MISSION_JOB_STATUSES = MISSION_JOB_STATUSES.NEW
  ): Promise<MissionJob> => {
    const task = await MainService.mainMissionJobPartialUpdate(task_id, {
      robot_id: robotId,
      mission_id: missionId,
      name: name,
      scheduler_settings: scheduler_settings,
      status: status,
    })
    return task
  },

  updateTheEntireOdyssey: async (
    odyssey_id: number,
    missionIdArray: number[],
    robotId: number,
    scheduler_settings?: SchedulerSettings,
    name?: string,
    scenario?: number | null,
    status: MISSION_JOB_STATUSES = MISSION_JOB_STATUSES.NEW
  ): Promise<Odyssey> => {
    const updateData: any = {
      robot_id: robotId,
      missions: missionIdArray,
      name: name,
      scheduler_settings: scheduler_settings,
      status: status,
      scenario: scenario,
    }
    const odyssey = await MainService.mainOdysseyPartialUpdate(odyssey_id, updateData)
    return odyssey
  },
  updateMissionJobType: async (
    task_id: number,
    job_type: MissionJobTypeEnum,
    status: MISSION_JOB_STATUSES = MISSION_JOB_STATUSES.NEW
  ): Promise<MissionJob> => {
    const task = await MainService.mainMissionJobPartialUpdate(task_id, {
      job_type: job_type,
      status: status,
    })
    return task
  },

  updateOdysseyType: async (
    task_id: number,
    job_type: MissionJobTypeEnum,
    status: MISSION_JOB_STATUSES = MISSION_JOB_STATUSES.NEW
  ): Promise<Odyssey> => {
    const odyssey = await MainService.mainOdysseyPartialUpdate(task_id, {
      job_type: job_type,
      status: status,
    })
    return odyssey
  },

  uploadJobToRobot: async (jobId: number) => {
    await MainService.mainMissionJobStartCalculationCreate(jobId)
  },

  uploadOdysseyToRobot: async (odysseyId: number) => {
    await MainService.mainOdysseyStartCalculationCreate(odysseyId)
  },

  switchMode: async (id: number, mode: ROBOT_MODES, metadata?: MappingMetadata) => {
    const url = `/api/main/robot/${id}/switch-mode/${String(mode)}/`
    const options: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }

    // Add body only if metadata is provided
    // Empty body means legacy mode
    if (metadata) {
      options.body = JSON.stringify(metadata)
    }

    const response = await fetch(url, options)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to switch mode' }))
      throw new Error(errorData.detail || errorData.message || 'Failed to switch mode')
    }
  },
  execCommand: async (id: number, command: ROBOT_COMMANDS) => {
    await MainService.mainRobotExecCommandCreate(id, String(command))
  },
  execEquipmentCommand: async (id: number, command: ROBOT_EQUIPMENT_COMMAND, value: string): Promise<void> => {
    await MainService.mainRobotExecEquipmentCommandCreate(command, id, value)
  },
}
